import { useEffect, useMemo, useState, type JSX } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, type PromoCode, type Product, type PromoCodeUsageGlobal } from '../api.js';
import { useActiveProfileId } from '../active-profile.js';
import { Icon } from '../components/Icon.js';
import { Topbar } from './AdminLayout.js';
import { fmtAmount, fmtDate } from '../utils/format.js';
import { isolate } from '../utils/bidi.js';
import { fmtIsraelDate } from '../utils/israel-time.js';

function discountLabel(p: PromoCode): string {
  if (p.discountType === 'percent') return `${p.percentValue ?? 0}%`;
  // fixed — может быть в обеих валютах одновременно
  const parts: string[] = [];
  if (p.fixedIls && p.fixedIls > 0) {
    parts.push('−' + (p.fixedIls / 100).toFixed(2).replace(/\.00$/, '') + ' ₪');
  }
  if (p.fixedRub && p.fixedRub > 0) {
    parts.push('−' + (p.fixedRub / 100).toFixed(2).replace(/\.00$/, '') + ' ₽');
  }
  return parts.join(' / ');
}

function productsLabel(p: PromoCode, products: Product[]): string {
  if (p.appliesToAllProducts) return 'Все продукты';
  if (p.productIds.length === 0) return '— (нужно выбрать)';
  const names = p.productIds
    .map((id) => isolate(products.find((pr) => pr.id === id)?.name ?? id.slice(0, 8)))
    .slice(0, 2);
  const extra = p.productIds.length > 2 ? ` +${p.productIds.length - 2}` : '';
  return names.join(', ') + extra;
}

function expiryLabel(p: PromoCode): string {
  if (!p.expiresAt) return 'Бессрочно';
  return 'до ' + fmtIsraelDate(p.expiresAt);
}

function usesLabel(p: PromoCode): string {
  if (p.maxUses === null) return `${p.usedCount} / ∞`;
  return `${p.usedCount} / ${p.maxUses}`;
}

type SortKey = 'code' | 'discount' | 'products' | 'expiry' | 'uses';

/** Числовое значение скидки для сортировки (% либо фикс в major-единицах). */
function discountValue(p: PromoCode): number {
  if (p.discountType === 'percent') return p.percentValue ?? 0;
  return ((p.fixedIls ?? 0) || (p.fixedRub ?? 0)) / 100;
}

/** Срок как timestamp; «Бессрочно» (null) уходит в конец при asc. */
function expiryValue(p: PromoCode): number {
  return p.expiresAt ? new Date(p.expiresAt).getTime() : Number.POSITIVE_INFINITY;
}

/** Что передаёт страница промокода после сохранения. */
interface SavedPromo {
  code: string;
  created: boolean;
  copied: boolean;
}

export function PromoCodesList(): JSX.Element {
  const nav = useNavigate();
  const location = useLocation();
  // Плашка «сохранён» после возврата со страницы промокода: показываем и
  // стираем из истории, чтобы при обновлении страницы она не появлялась снова.
  const [saved, setSaved] = useState<SavedPromo | null>(
    () => (location.state as { savedPromo?: SavedPromo } | null)?.savedPromo ?? null,
  );
  useEffect(() => {
    if (!saved) return;
    nav(location.pathname, { replace: true, state: null });
    const t = setTimeout(() => setSaved(null), 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [items, setItems] = useState<PromoCode[] | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [usages, setUsages] = useState<PromoCodeUsageGlobal[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'active' | 'inactive' | 'usages'>('active');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [activeProfileId] = useActiveProfileId();
  useEffect(() => {
    setItems(null);
    setUsages(null);
    setErr(null);
    Promise.all([api.listPromoCodes(), api.listProducts(), api.listPromoCodeUsages()])
      .then(([promos, prods, uses]) => {
        setItems(promos);
        setProducts(prods);
        setUsages(uses);
      })
      .catch((e: Error) => setErr(e.message));
  }, [activeProfileId]);

  const counts = useMemo(() => {
    if (!items) return { active: 0, inactive: 0 };
    let active = 0;
    let inactive = 0;
    for (const p of items) p.isActive ? active++ : inactive++;
    return { active, inactive };
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.toLowerCase();
    const wantActive = tab === 'active';
    return items.filter(
      (p) => p.isActive === wantActive && (p.code + ' ' + p.description).toLowerCase().includes(s),
    );
  }, [items, q, tab]);

  // Сортировка по клику на заголовок. Пока столбец не выбран — порядок как с API.
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'code':
          cmp = a.code.localeCompare(b.code, 'ru');
          break;
        case 'discount':
          cmp = discountValue(a) - discountValue(b);
          break;
        case 'products':
          cmp = productsLabel(a, products).localeCompare(productsLabel(b, products), 'ru');
          break;
        case 'expiry':
          cmp = expiryValue(a) - expiryValue(b);
          break;
        case 'uses':
          cmp = a.usedCount - b.usedCount;
          break;
      }
      return cmp * dir;
    });
  }, [filtered, sortKey, sortDir, products]);

  const toggleSort = (key: SortKey): void => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const renderTh = (key: SortKey, label: string): JSX.Element => (
    <th
      onClick={() => toggleSort(key)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {label}{' '}
      <span style={{ opacity: sortKey === key ? 0.9 : 0.25, fontSize: 11 }}>
        {sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </th>
  );

  const succeededUsages = useMemo(
    () => (usages ?? []).filter((u) => u.status === 'succeeded').length,
    [usages],
  );

  return (
    <>
      <Topbar
        title="Промокоды"
        actions={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => nav('/admin/promo-codes/new')}
          >
            <Icon name="plus" size={13} />
            Новый промокод
          </button>
        }
      />
      <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
        {saved && (
          <div
            className="card"
            role="status"
            style={{
              padding: '12px 14px',
              marginBottom: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 14,
            }}
          >
            <Icon name="check" size={15} />
            <span style={{ flex: 1 }}>
              Промокод <b className="mono">{saved.code}</b> {saved.created ? 'создан' : 'сохранён'}
              {saved.copied ? ', код скопирован в буфер обмена.' : '.'}
            </span>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setSaved(null)}>
              <Icon name="x" size={13} />
            </button>
          </div>
        )}
        {err && (
          <div
            className="card"
            style={{
              padding: 14,
              marginBottom: 14,
              color: 'var(--danger-fg)',
              background: 'var(--danger-subtle-bg)',
            }}
          >
            {err}
          </div>
        )}
        <div className="card">
          <div style={{ padding: '0 20px' }}>
            <div className="tabs">
              <button
                type="button"
                className={'tab ' + (tab === 'active' ? 'active' : '')}
                onClick={() => setTab('active')}
              >
                Активные {items ? `(${counts.active})` : ''}
              </button>
              <button
                type="button"
                className={'tab ' + (tab === 'inactive' ? 'active' : '')}
                onClick={() => setTab('inactive')}
              >
                Выключенные {items ? `(${counts.inactive})` : ''}
              </button>
              <button
                type="button"
                className={'tab ' + (tab === 'usages' ? 'active' : '')}
                onClick={() => setTab('usages')}
              >
                Использования {usages ? `(${usages.length})` : ''}
              </button>
            </div>
          </div>

          {/* Tabs: ACTIVE / INACTIVE (списки промокодов) */}
          {(tab === 'active' || tab === 'inactive') && (
            <>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--hairline)' }}>
                <input
                  type="text"
                  className="input"
                  placeholder="Поиск по коду или описанию…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  style={{ maxWidth: 360 }}
                />
              </div>
              {items === null ? (
                <div style={{ padding: 24, color: 'var(--fg-subtle)' }}>Загрузка…</div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: 24, color: 'var(--fg-subtle)' }}>
                  {q ? 'Ничего не найдено' : 'Пока промокодов нет'}
                </div>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      {renderTh('code', 'Код')}
                      {renderTh('discount', 'Скидка')}
                      {renderTh('products', 'Продукты')}
                      {renderTh('expiry', 'Срок')}
                      {renderTh('uses', 'Использований')}
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((p) => (
                      <tr
                        key={p.id}
                        onClick={() => nav(`/admin/promo-codes/${p.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <div style={{ fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
                            {p.code}
                          </div>
                          {p.description && (
                            <div
                              style={{
                                fontSize: 12,
                                color: 'var(--fg-subtle)',
                                marginTop: 2,
                              }}
                            >
                              <bdi>{p.description}</bdi>
                            </div>
                          )}
                        </td>
                        <td>{discountLabel(p)}</td>
                        <td style={{ maxWidth: 240, fontSize: 13 }}>
                          {productsLabel(p, products)}
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
                          {expiryLabel(p)}
                        </td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                          {usesLabel(p)}
                        </td>
                        <td style={{ width: 1, whiteSpace: 'nowrap' }}>
                          <Icon name="chevron" size={14} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {/* Tab: USAGES (агрегированная таблица применений) */}
          {tab === 'usages' && (
            <>
              {usages === null ? (
                <div style={{ padding: 24, color: 'var(--fg-subtle)' }}>Загрузка…</div>
              ) : usages.length === 0 ? (
                <div style={{ padding: 24, color: 'var(--fg-subtle)' }}>
                  Никто ещё не применял промокоды в этом профиле.
                </div>
              ) : (
                <>
                  <div
                    style={{
                      padding: '14px 20px',
                      borderBottom: '1px solid var(--hairline)',
                      fontSize: 13,
                      color: 'var(--fg-subtle)',
                    }}
                  >
                    Всего применений: {usages.length} · из них оплачено: {succeededUsages}
                  </div>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Когда</th>
                        <th>Промокод</th>
                        <th>Клиент</th>
                        <th>Продукт</th>
                        <th>Сумма</th>
                        <th>Статус</th>
                        <th>Билет</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usages.map((u) => (
                        <tr
                          key={u.id}
                          onClick={() => u.promoCodeId && nav(`/admin/promo-codes/${u.promoCodeId}`)}
                          style={{ cursor: u.promoCodeId ? 'pointer' : 'default' }}
                        >
                          <td style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                            {fmtDate(u.createdAt)}
                          </td>
                          <td>
                            <span
                              className="mono"
                              style={{ fontSize: 13, fontWeight: 600 }}
                            >
                              {u.promoCode?.code ?? '—'}
                            </span>
                          </td>
                          <td>
                            <div style={{ fontSize: 13 }}><bdi>{u.customerName}</bdi></div>
                            <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>
                              {u.customerEmail}
                            </div>
                          </td>
                          <td style={{ fontSize: 13 }}><bdi>{u.product?.name ?? '—'}</bdi></td>
                          <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                            {u.originalAmount && u.discountAmount ? (
                              <>
                                <span
                                  style={{
                                    color: 'var(--fg-subtle)',
                                    textDecoration: 'line-through',
                                    marginRight: 6,
                                  }}
                                >
                                  {fmtAmount(u.originalAmount, u.currency)}
                                </span>
                                {fmtAmount(u.amount, u.currency)}
                              </>
                            ) : (
                              fmtAmount(u.amount, u.currency)
                            )}
                          </td>
                          <td style={{ fontSize: 12 }}>{u.status}</td>
                          <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                            {u.ticketNumber ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
